/** New elements with sensible broadcast defaults. Pure: ids come from the scene. */
import type { Asset, Element, Scene } from '@prism-graphics/scene-schema';
import { uniqueId } from './ids.ts';
import { allIds, walk } from './tree.ts';

export type NewElementKind = 'text' | 'rect' | 'ellipse' | 'line' | 'box' | 'stack' | 'image' | 'video';

const TRANSFORM = { x: 0, y: 0, rotation: 0, scaleX: 1, scaleY: 1, originX: 0.5, originY: 0.5, opacity: 1 };
const AUTO = { width: 'auto' as const, height: 'auto' as const };

const PREFIX: Record<NewElementKind, [string, string]> = {
  text: ['text', 'Text'],
  rect: ['rect', 'Rectangle'],
  ellipse: ['ellipse', 'Ellipse'],
  line: ['line', 'Line'],
  box: ['box', 'Box'],
  stack: ['stack', 'Stack'],
  image: ['image', 'Image'],
  video: ['video', 'Video'],
};

/** "Text 3" for the third text element, counting existing names. */
function nextName(scene: Scene, label: string): string {
  const names = new Set<string>();
  walk(scene.elements, (el) => names.add(el.name));
  for (let n = 1; ; n++) if (!names.has(`${label} ${n}`)) return `${label} ${n}`;
}

export interface NewElementOptions {
  /** Font asset hash for text. */
  font?: string;
  /** Image or video asset. */
  asset?: Asset;
  /** Pixel size of an image (from the library), to size the element. */
  naturalSize?: { width: number; height: number };
  x?: number;
  y?: number;
}

export function createElement(scene: Scene, kind: NewElementKind, opts: NewElementOptions = {}): Element {
  const [prefix, label] = PREFIX[kind];
  const id = uniqueId(prefix, allIds(scene));
  const base = { id, name: nextName(scene, label), locked: false, visible: true, transform: { ...TRANSFORM, x: opts.x ?? 0, y: opts.y ?? 0 } };
  switch (kind) {
    case 'text':
      if (!opts.font) throw new Error('a text element needs a font');
      return {
        ...base,
        type: 'text',
        size: { ...AUTO },
        text: 'Text',
        style: { font: opts.font, fontWeight: 600, fontStyle: 'normal', fontSize: 56, lineHeight: 1.2, letterSpacing: 0, textTransform: 'none', textAlign: 'left', color: '#FFFFFF' },
        overflow: { mode: 'none' },
      };
    case 'rect':
      return { ...base, type: 'rect', size: { width: 480, height: 120 }, style: { fill: { type: 'solid', color: '#00B4C8' } } };
    case 'ellipse':
      return { ...base, type: 'ellipse', size: { width: 200, height: 200 }, style: { fill: { type: 'solid', color: '#00B4C8' } } };
    case 'line':
      return { ...base, type: 'line', size: { width: 480, height: 'auto' }, stroke: { color: '#FFFFFF', width: 4 }, cap: 'butt' };
    case 'box':
      // An auto-size box: hugs its content with padding, between a minimum and a maximum width (a name bar).
      return {
        ...base,
        type: 'group',
        size: { ...AUTO, minWidth: 240, maxWidth: 1200 },
        layout: { mode: 'stack', stack: { direction: 'horizontal', gap: 16, align: 'center', justify: 'start' }, padding: { top: 12, right: 32, bottom: 12, left: 32 } },
        style: { fill: { type: 'solid', color: '#00B4C8' } },
        clipContent: true,
        children: [],
      };
    case 'stack':
      return {
        ...base,
        type: 'group',
        size: { ...AUTO },
        layout: { mode: 'stack', stack: { direction: 'vertical', gap: 0, align: 'start', justify: 'start' } },
        style: {},
        clipContent: false,
        children: [],
      };
    case 'image':
    case 'video': {
      if (!opts.asset) throw new Error(`an ${kind} element needs an asset`);
      const n = opts.naturalSize;
      const scale = n ? Math.min(1, (scene.canvas.width * 0.5) / n.width, (scene.canvas.height * 0.5) / n.height) : 1;
      const size = n ? { width: Math.round(n.width * scale), height: Math.round(n.height * scale) } : { width: 320, height: 180 };
      return kind === 'image'
        ? { ...base, type: 'image', size, asset: opts.asset.hash, fit: 'contain' }
        : { ...base, type: 'video', size, asset: opts.asset.hash, fit: 'contain', loop: true };
    }
  }
}

/** Approximate size of a new element before it is rendered (for placement). */
export function nominalSize(el: Element): { width: number; height: number } {
  const w = typeof el.size.width === 'number' ? el.size.width : el.type === 'text' ? el.style.fontSize * 3 : 240;
  const h = typeof el.size.height === 'number' ? el.size.height : el.type === 'text' ? el.style.fontSize * el.style.lineHeight : el.type === 'line' ? el.stroke.width : 80;
  return { width: w, height: h };
}
